// Story 8.1 (ADO-2010): the pure rules over `GET /api/v1/ordering/choices`.
//
// No I/O and no store access, so the entry flow (A1.1, A1.4, A3-A5), the destination
// rule (A10.1, A10.2) and the switcher's option list (A9.1) are unit-testable alone.
// Everything here is a routing / display hint: the server re-checks the depot of every
// basket call (D-A), so a resolved choice is never a permission.

import type {
  OpenCart,
  OrderingAccount,
  OrderingChoices,
  OrderingEntry,
} from '../api/ordering-choices.client';
import type { LandingType } from '../api/landing.client';
import { cartTargetKey, sameCartTarget, type CartTarget } from '../api/cart-target';
import type { CutOffDepot } from '../basket/nextCutOffs';

/** A chosen entry (and Trading Account, when the entry has accounts). */
export interface OrderingSelection {
  readonly entryKey: string;
  readonly customerAccountCode: string | null;
}

export interface ResolvedChoice {
  readonly entry: OrderingEntry;
  /** The chosen Trading Account; `null` for a staff entry (no accounts). */
  readonly account: OrderingAccount | null;
}

/**
 * Resolve `entryKey` (+ account) against the OFFERED set. `null` when the entry is not
 * offered, when the account is not at that entry (one the Customer no longer holds there),
 * or when a multi-account entry is named without an account (A4.1, Story 8.4 AC1: the Shopper
 * must pick one, as at a Value Centre offering several). A single-account entry named without
 * a code resolves to its sole account.
 */
export function resolveChoice(
  choices: OrderingChoices,
  entryKey: string,
  customerAccountCode: string | null,
): ResolvedChoice | null {
  const entry = choices.entries.find((candidate) => candidate.key === entryKey);
  if (entry === undefined) return null;
  if (entry.accounts.length === 0) return { entry, account: null };
  if (customerAccountCode === null) {
    return entry.accounts.length === 1 ? { entry, account: entry.accounts[0] ?? null } : null;
  }
  const account = entry.accounts.find(
    (candidate) => candidate.customerAccountCode === customerAccountCode,
  );
  return account === undefined ? null : { entry, account };
}

/**
 * Story 8.4 (ADO-2013) and Enabler E8-G (ADO-3109), DA-8: the cart an entry and account are
 * reached through, the depot, the account and the entry's kind. Every per-cart read, write and
 * record goes through it, so Chill and Fruit & Veg at depot 25 are never one cart.
 */
export function cartTargetOf(
  entry: Pick<OrderingEntry, 'depotId' | 'kind'>,
  customerAccountCode: string | null,
): CartTarget {
  return { depotId: entry.depotId, customerAccountCode, entryKind: entry.kind };
}

/**
 * The one place a store user can order at, or `null` when there is a real choice to make: the
 * one entry and account when the offered set holds a single cart. Enabler E8-G (ADO-3109), DA-8
 * (AC9): every entry is a cart of its own (its depot, account and kind), Fruit & Veg and WOW
 * included, so a Customer offered Chill and Fruit & Veg has two carts to choose between, as SL1.0
 * shows them (`engine/get_smanager.php:4602-4615`: Fruit & Veg is its own tile after Chill; and
 * `index.php:214-219`: SL1.0 always opens the choice page when no environment is chosen). Staff
 * (`DEPOT_IDS`) are never resolved here.
 */
export function singleOrderingTarget(choices: OrderingChoices): ResolvedChoice | null {
  if (choices.scope !== 'TRADING_ACCOUNTS') return null;
  const targets = new Map<string, ResolvedChoice>();
  for (const entry of choices.entries) {
    if (entry.accounts.length === 0) {
      targets.set(cartTargetKey(cartTargetOf(entry, null)), { entry, account: null });
      continue;
    }
    for (const account of entry.accounts) {
      const id = cartTargetKey(cartTargetOf(entry, account.customerAccountCode));
      if (!targets.has(id)) targets.set(id, { entry, account });
    }
  }
  const [only, ...rest] = [...targets.values()];
  return only !== undefined && rest.length === 0 ? only : null;
}

/**
 * The choice made for the Shopper at sign-in, or `null` to show the choice screen:
 *   - a remembered hint still in the offered set: straight to it (A1.1, A1.4);
 *   - a single ordering target (`singleOrderingTarget`): straight in to it (A1.1, A3);
 *   - otherwise the choice screen (A1.1, A4, A5), including an unlinked Shopper (A2.2).
 * Staff (`DEPOT_IDS`) keep today's behaviour and are never auto-resolved here.
 */
export function resolveInitialChoice(
  choices: OrderingChoices,
  hint: OrderingSelection | null,
): OrderingSelection | null {
  if (choices.scope !== 'TRADING_ACCOUNTS') return null;
  const remembered =
    hint === null ? null : resolveChoice(choices, hint.entryKey, hint.customerAccountCode);
  const resolved = remembered ?? singleOrderingTarget(choices);
  return resolved === null
    ? null
    : {
        entryKey: resolved.entry.key,
        customerAccountCode: resolved.account?.customerAccountCode ?? null,
      };
}

const LANDING_TYPES: Readonly<Record<OrderingAccount['tradingAccountType'], LandingType | null>> = {
  FOODSERVICE: 'FOODSERVICE',
  LICENSED_TRADE: 'LICENSED_TRADE',
  LSN: 'LSN',
  DEFAULT: null,
};

/** The landing page type for the active account, or `null` for the product list (A10.2). */
export function landingTypeFor(account: OrderingAccount | null): LandingType | null {
  return account === null ? null : LANDING_TYPES[account.tradingAccountType];
}

export const LANDING_ROUTE = '/landing';
export const PRODUCTS_ROUTE = '/products';
export const PRODUCT_DETAIL_ROUTE = '/products/:sku';
export const CHOOSE_ROUTE = '/choose';

/** Where choosing a Cart leads (A10.1): the landing page for a landing type, else the PLP. */
export function destinationFor(account: OrderingAccount | null): string {
  return landingTypeFor(account) === null ? PRODUCTS_ROUTE : LANDING_ROUTE;
}

/**
 * The entry a depot stands for when only the depot is known (a depot picker): its `DEPOT` entry,
 * else the first entry at it. Several entries can share a depot (Chill and Fruit & Veg; a Value
 * Centre that also holds an LSN account, whose LSN entry sorts first). A cart is never looked up
 * this way: it names its entry kind (`entryForCart`).
 */
export function primaryEntryAtDepot(
  entries: readonly OrderingEntry[],
  depotId: string,
): OrderingEntry | null {
  const atDepot = entries.filter((entry) => entry.depotId === depotId);
  return atDepot.find((entry) => entry.kind === 'DEPOT') ?? atDepot[0] ?? null;
}

/**
 * Story 8.4 (ADO-2013): the entry a cart is taken up through. Enabler E8-G (ADO-3109), DA-8: a
 * cart is keyed by its depot, its Trading Account and its entry kind, so it is the entry of that
 * kind at that depot (a Value Centre's `DEPOT` entry for its `DEPOT` accounts, its `LSN` entry
 * for the `LSN` one; Chill's for the Chill cart, Fruit & Veg's for the Fruit & Veg one) that
 * offers that account. DA-10 (AC11): a staff entry offers no account (staff pick a store account
 * from a search), so a staff cart is taken up through the entry of its kind at its depot. `null`
 * when no entry offers the cart.
 */
export function entryForCart(
  entries: readonly OrderingEntry[],
  cart: CartTarget,
): OrderingEntry | null {
  return (
    entries.find(
      (entry) =>
        entry.depotId === cart.depotId &&
        entry.kind === cart.entryKind &&
        (entry.accounts.length === 0 ||
          cart.customerAccountCode === null ||
          entry.accounts.some(
            (account) => account.customerAccountCode === cart.customerAccountCode,
          )),
    ) ?? null
  );
}

/**
 * Story 8.5 (ADO-2014), D-20, and Story 8.4 (ADO-2013): what the "Next cut-off" line and list
 * read and name. One per cart, in option order: each Value Centre account is a target of its
 * own, named "<centre> · <account name>" (its number when the name is blank); any other target is
 * named by its entry. Enabler E8-G (ADO-3109), DA-8: Chill and Fruit & Veg are two carts, so two
 * targets ("Chill" and "Fruit & Veg"). They share one cut-off read, as the cut-off is read per
 * depot and account (SL1.0 reads one delivery schedule for both: Fruit & Veg runs on Chill's depot
 * 25, `engine/get_smanager.php:5563-5565`, and the schedule is keyed by the depot and the store,
 * `:5673-5676`).
 */
export function cutOffTargets(choices: OrderingChoices): CutOffDepot[] {
  const targets = new Map<string, CutOffDepot>();
  for (const option of choiceOptions(choices)) {
    const cart = cartTargetOf(option.entry, option.customerAccountCode);
    const key = cartTargetKey(cart);
    if (targets.has(key)) continue;
    const entry = option.entry;
    const name =
      entry.isValueCentre && option.account !== null
        ? `${entry.label} · ${option.account.accountName.trim() || option.account.customerAccountCode}`
        : entry.label;
    targets.set(key, { ...cart, name });
  }
  return [...targets.values()];
}

/** The open cart of `cart` (its depot, its account, Story 8.4 ADO-2013, and its entry kind,
 *  Enabler E8-G ADO-3109 DA-8: Chill's and Fruit & Veg's are two), or `null`. */
export function openCartFor(openCarts: readonly OpenCart[], cart: CartTarget): OpenCart | null {
  return openCarts.find((candidate) => sameCartTarget(candidate, cart)) ?? null;
}

/** The option id for an entry + account pair (unique across the switcher). */
export function choiceId(entryKey: string, customerAccountCode: string | null): string {
  return customerAccountCode === null ? entryKey : `${entryKey}|${customerAccountCode}`;
}

/** One pickable (entry, account) pair: a switcher row or a choice-screen tile. */
export interface ChoiceOption {
  readonly id: string;
  readonly entryKey: string;
  readonly customerAccountCode: string | null;
  readonly entry: OrderingEntry;
  readonly account: OrderingAccount | null;
  /** The entry label ("VC Dublin", "Fruit & Veg"). */
  readonly title: string;
  /** "Main grocery · 3205484" at an entry that names its account (`namesItsAccount`: every
   *  Value Centre account), `null` at any other entry and for a staff entry. */
  readonly detail: string | null;
  /** Cases in the option's open cart, its depot and account (0 when none). */
  readonly caseCount: number;
  /** Lines in the option's open cart (0 when none): the Depot Switcher's count badge. */
  readonly lineCount: number;
}

/** The account's display line: "name · number", or the number when the name is blank. */
export function accountLine(account: OrderingAccount): string {
  return account.accountName.trim() === ''
    ? account.customerAccountCode
    : `${account.accountName} · ${account.customerAccountCode}`;
}

/**
 * Whether the entry names its account (the account line) wherever it is shown: at any Value
 * Centre entry with accounts, even when the Customer holds only one there (Story 8.4, ADO-2013;
 * Figma "VC Wicklow" names its one account), so the Shopper sees which account the order goes
 * to; and at any other entry offering several (A4.2: no two options with the same label). A
 * core depot offering one account names none, however many the Customer holds there.
 */
export function namesItsAccount(entry: OrderingEntry): boolean {
  if (entry.accounts.length > 1) return true;
  return entry.isValueCentre && entry.accounts.length > 0;
}

/** The account as a cart's name gives it: its name, or its number when the name is blank. */
export function accountNameOf(account: OrderingAccount): string {
  return account.accountName.trim() || account.customerAccountCode;
}

/**
 * Story 8.4 (ADO-2013): a cart as its order is named ("VC Dublin · Main grocery"): the entry's
 * label, with the account's name after it (its number when the name is blank) where the entry
 * names its account (`namesItsAccount`). The label alone with no account.
 */
export function cartName(
  entry: OrderingEntry,
  account: OrderingAccount | null | undefined,
): string {
  if (account === null || account === undefined || !namesItsAccount(entry)) return entry.label;
  return `${entry.label} · ${accountNameOf(account)}`;
}

/** Every pickable option, in entry order: one per account (A4.1), one per staff entry. */
export function choiceOptions(choices: OrderingChoices): ChoiceOption[] {
  return choices.entries.flatMap((entry): ChoiceOption[] => {
    // Story 8.4 (ADO-2013): each option counts its own cart, the depot AND the account.
    const counts = (customerAccountCode: string | null) => {
      const openCart = openCartFor(choices.openCarts, cartTargetOf(entry, customerAccountCode));
      return { caseCount: openCart?.caseCount ?? 0, lineCount: openCart?.lineCount ?? 0 };
    };
    if (entry.accounts.length === 0) {
      return [
        {
          id: choiceId(entry.key, null),
          entryKey: entry.key,
          customerAccountCode: null,
          entry,
          account: null,
          title: entry.label,
          detail: null,
          ...counts(null),
        },
      ];
    }
    const named = namesItsAccount(entry);
    return entry.accounts.map((account) => ({
      id: choiceId(entry.key, account.customerAccountCode),
      entryKey: entry.key,
      customerAccountCode: account.customerAccountCode,
      entry,
      account,
      title: entry.label,
      detail: named ? accountLine(account) : null,
      ...counts(account.customerAccountCode),
    }));
  });
}

/** A choice-screen tile: the option and the two lines it shows. */
export interface ChoiceTile {
  readonly option: ChoiceOption;
  readonly title: string;
  readonly detail: string | null;
}

/** The choice screen's two tile sections (Figma "Select Depot", 5031:4205). */
export interface ChoiceSections {
  /** "Shop by depot": every entry that is not a Value Centre (the core depots and the
   *  Fruit & Veg, WOW, Corrib Retail and LSN ranges), titled by the entry. At such an entry
   *  with several accounts each account is its own tile with its account line (A4.2: no
   *  silent pick). Enabler E8-G (ADO-3109), task 2.6: also the Value Centres SL1.0 shows as
   *  tiles of their own (`showsAsOwnTile`: 4 Aces, Better Deal, Corrib Foods, Collooney), each
   *  account a tile with its account line, as every Value Centre names its account. */
  readonly byDepot: readonly ChoiceTile[];
  /** "Select your VC depot": one tile per account of every other Value Centre entry, titled by
   *  the VC name with the account line ("<account name> · <account number>") under it, whatever
   *  the account count. A staff Value Centre entry (no accounts) is one tile titled by the
   *  entry. */
  readonly valueCentres: readonly ChoiceTile[];
}

/** Enabler E8-G (ADO-3109), task 2.6: whether the entry is listed under "Select your VC depot":
 *  a Value Centre SL1.0 lists in its Value Centre tile, which leaves out the four it shows as
 *  tiles of their own (`engine/get_smanager.php:4591`). */
export function inValueCentreSection(entry: OrderingEntry): boolean {
  return entry.isValueCentre && entry.showsAsOwnTile !== true;
}

/** Split the options into the choice screen's sections, keeping their order. */
export function choiceSections(options: readonly ChoiceOption[]): ChoiceSections {
  const byDepot: ChoiceTile[] = [];
  const valueCentres: ChoiceTile[] = [];
  for (const option of options) {
    const { account, entry } = option;
    if (!inValueCentreSection(entry)) {
      byDepot.push({
        option,
        title: option.title,
        detail: account !== null && namesItsAccount(entry) ? accountLine(account) : null,
      });
    } else if (account === null) {
      valueCentres.push({ option, title: option.title, detail: null });
    } else {
      valueCentres.push({ option, title: option.title, detail: accountLine(account) });
    }
  }
  return { byDepot, valueCentres };
}
